import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Download, Trophy } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "/nphs-logo.png";

const RANK_LABEL = {
  1: "#1 · School Record",
  2: "#2 All-Time",
  3: "#3 All-Time",
};

/**
 * RecordShareCard — 1080x1080 Instagram-ready image generator for a broken record.
 * Renders an offscreen "poster" template and lets the coach download it as PNG.
 */
export default function RecordShareCard({ record, compact = false }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0A0A0A",
        scale: 1,
        useCORS: true,
        logging: false,
        width: 1080,
        height: 1080,
        windowWidth: 1080,
        windowHeight: 1080,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      const stamp = `${record.player_name.replace(/\s+/g, "-")}-${record.category}-${record.date}`;
      a.href = dataUrl;
      a.download = `nphs-record-${stamp}.png`;
      a.click();
      // Also copy to clipboard when supported
      try {
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        if (blob && navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          toast.success("Card downloaded + copied to clipboard");
          return;
        }
      } catch { /* clipboard-image unsupported (Safari etc.) — that's fine */ }
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Couldn't generate card");
    } finally {
      setBusy(false);
    }
  };

  const rankLabel = RANK_LABEL[record.rank] || `#${record.rank}`;
  const vsPrefix = record.home_away === "away" ? "@" : "vs";

  return (
    <>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        className={compact
          ? "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-black/40 hover:bg-black/60 text-amber-200 hover:text-white border border-amber-400/40 rounded px-2 py-1 transition disabled:opacity-50"
          : "inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-amber-400 hover:bg-amber-300 text-burgundy font-bold rounded px-3 py-2 transition disabled:opacity-50"}
        data-testid={`record-share-card-${record.category}`}
      >
        <Download className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
        {busy ? "Generating…" : "Share card"}
      </button>

      {/* Offscreen 1080x1080 template — positioned way off-viewport but rendered */}
      <div style={{ position: "fixed", left: "-99999px", top: 0, pointerEvents: "none" }} aria-hidden>
        <div
          ref={cardRef}
          style={{
            width: 1080,
            height: 1080,
            background:
              "radial-gradient(circle at 78% 18%, rgba(255,193,7,0.35) 0%, transparent 45%), radial-gradient(circle at 15% 85%, rgba(107,15,26,0.9) 0%, transparent 60%), linear-gradient(135deg, #0b0b0f 0%, #1a0509 60%, #6B0F1A 100%)",
            padding: 72,
            fontFamily: "'Space Grotesk', 'Bebas Neue', Impact, sans-serif",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* Grain / diagonal texture */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "repeating-linear-gradient(135deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 8px)",
            }}
          />

          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              <img src={LOGO_URL} alt="" crossOrigin="anonymous" style={{ width: 88, height: 88, objectFit: "contain" }} />
              <div>
                <p style={{ margin: 0, fontSize: 15, letterSpacing: "0.28em", textTransform: "uppercase", color: "#e5e5e5", fontWeight: 700 }}>NPHS Canucks</p>
                <p style={{ margin: 0, fontSize: 13, letterSpacing: "0.28em", textTransform: "uppercase", color: "#a3a3a3", fontWeight: 600 }}>Boys Basketball</p>
              </div>
            </div>
            <div
              style={{
                background: "#FFC107",
                color: "#6B0F1A",
                fontWeight: 900,
                fontSize: 18,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                padding: "10px 20px",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ fontSize: 22 }}>🏆</span>
              Record Book
            </div>
          </div>

          {/* Rank stripe */}
          <div style={{ marginTop: 76, marginBottom: 32, position: "relative", zIndex: 2 }}>
            <div
              style={{
                display: "inline-block",
                background: record.rank === 1 ? "#FFC107" : "rgba(255,193,7,0.2)",
                color: record.rank === 1 ? "#6B0F1A" : "#FFC107",
                padding: "12px 28px",
                fontSize: 26,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                border: record.rank !== 1 ? "2px solid #FFC107" : "none",
              }}
            >
              {rankLabel}
            </div>
          </div>

          {/* Player name — hero */}
          <h1
            style={{
              margin: 0,
              fontSize: 116,
              lineHeight: 0.92,
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: "-0.02em",
              position: "relative",
              zIndex: 2,
              maxWidth: 900,
              wordBreak: "break-word",
            }}
          >
            {record.player_name}
          </h1>

          {/* Big stat */}
          <div style={{ marginTop: 60, display: "flex", alignItems: "flex-end", gap: 28, position: "relative", zIndex: 2 }}>
            <p
              style={{
                margin: 0,
                fontSize: 268,
                lineHeight: 0.85,
                fontWeight: 900,
                color: "#FFC107",
                letterSpacing: "-0.04em",
                textShadow: "0 8px 40px rgba(0,0,0,0.4)",
              }}
            >
              {record.value}
            </p>
            <div style={{ paddingBottom: 24 }}>
              <p style={{ margin: 0, fontSize: 60, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.02em" }}>{record.suffix}</p>
              <p style={{ margin: "6px 0 0", fontSize: 22, letterSpacing: "0.24em", textTransform: "uppercase", color: "#a3a3a3", fontWeight: 600 }}>
                {record.label}
              </p>
            </div>
          </div>

          {/* Footer meta */}
          <div style={{ position: "absolute", left: 72, right: 72, bottom: 60, zIndex: 2 }}>
            <div style={{ height: 3, background: "linear-gradient(90deg, #FFC107 0%, rgba(255,193,7,0) 60%)", marginBottom: 28 }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
              <div>
                <p style={{ margin: 0, fontSize: 15, letterSpacing: "0.24em", textTransform: "uppercase", color: "#a3a3a3", fontWeight: 700 }}>Set</p>
                <p style={{ margin: "4px 0 0", fontSize: 30, fontWeight: 900, textTransform: "uppercase", color: "#fff" }}>
                  {vsPrefix} {record.opponent}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 18, color: "#d4d4d4", fontWeight: 600 }}>
                  {record.date}{record.season ? ` · ${record.season}` : ""}
                </p>
              </div>
              {record.jersey_number != null && (
                <div style={{ textAlign: "right" }}>
                  <p style={{ margin: 0, fontSize: 15, letterSpacing: "0.24em", textTransform: "uppercase", color: "#a3a3a3", fontWeight: 700 }}>Jersey</p>
                  <p style={{ margin: "4px 0 0", fontSize: 68, fontWeight: 900, color: "#FFC107", lineHeight: 1 }}>
                    #{record.jersey_number}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
